// utils/API/API.js
import axios from "axios";
import { isIosStandalone, setStoredTokens } from "../platform";

let baseURL = "https://testing-endpoint.partnersdash.com";

if (process.env.NODE_ENV === "development") {
  baseURL = ""; // same-origin in dev
}

// ─── iOS Standalone helpers ───────────────────────────────────────────────────
const getStoredAccessToken = () => localStorage.getItem("_at");
const getStoredRefreshToken = () => localStorage.getItem("_rt");

// ─── Axios instances ──────────────────────────────────────────────────────────
const api = axios.create({
  baseURL,
  withCredentials: true, // send _at/_rt cookies
});

// A dedicated instance for refresh so it doesn't go through interceptors
const refreshClient = axios.create({
  baseURL,
  withCredentials: true,
});

// ─── Request interceptor: attach Bearer for iOS standalone ────────────────────
api.interceptors.request.use((config) => {
  if (isIosStandalone()) {
    const token = getStoredAccessToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return config;
});

refreshClient.interceptors.request.use((config) => {
  if (isIosStandalone()) {
    const token = getStoredRefreshToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return config;
});

// single in-flight refresh promise (prevents race/dup refresh)
let refreshPromise = null;

// Helper: call refresh (returns a promise). If one is running, reuse it.
function ensureRefreshed() {
  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post("/auth/refresh")
      .then((res) => {
        // On iOS standalone, server returns new tokens in body — persist them
        if (isIosStandalone() && res.data?.accessToken) {
          setStoredTokens({
            accessToken: res.data.accessToken,
            refreshToken: res.data.refreshToken,
          });
        }
        return res.data;
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Heuristics to identify refreshable 401s
function isRefreshable401(error) {
  const status = error?.response?.status;
  if (status !== 401) return false;

  // Don't refresh for requests that already target /auth/refresh
  const url = error?.config?.url || "";
  if (url.includes("/auth/refresh")) return false;

  // Optionally exclude cases using an app code:
  // const code = error?.response?.data?.code;
  // if (code === "PIN_REQUIRED" || code === "ADMIN_ONLY") return false;

  return true;
}

function isPinNotVerified(error) {
  const status = error?.response?.status;
  return status === 428; // Precondition Required -> PIN step-up
}

// Axios RESPONSE interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;

    // 0) Network/CORS/TLS: no response object
    if (!error?.response) {
      console.error("Network-level failure:", error?.message);
      return Promise.reject(error);
    }

    // 1) PIN step-up first
    if (isPinNotVerified(error)) {
      const from = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.href = `/pin-auth?from=${from}`;
      return Promise.reject(error);
    }

    // 2) If NOT refreshable or already retried -> fail fast
    if (!isRefreshable401(error) || original?._retry) {
      return Promise.reject(error);
    }

    // 3) Refresh path
    original._retry = true;
    try {
      await ensureRefreshed();
      // Re-run the original request on the main client (now with fresh _at cookie)
      if (isIosStandalone()) {
        const freshToken = getStoredAccessToken();
        if (freshToken) {
          original.headers = {
            ...original.headers,
            Authorization: `Bearer ${freshToken}`,
          };
        }
      }
      return api(original);
    } catch (refreshErr) {
      if (refreshErr?.response?.status === 401) {
        window.location.href = "/";
      }
      return Promise.reject(refreshErr);
    }
  },
);

export default api;
